import { useEffect, useState, type RefObject } from 'react'
import { sidebarTipPosition, type TipPlace } from '@/lib/sidebar-size'

/** A key in the sidebar whose menu is open (the voice or user menu): while it is open no tooltip draws over it. */
const KEY_WITH_OPEN_LIST = '[aria-haspopup][aria-expanded="true"]'

interface Tip {
  top: number
  left: number
  label: string
  /** A second, quieter phrase: what the element is doing, when its name alone does not say. */
  note?: string
  keys?: string
  place: TipPlace
}

function tipFor(target: EventTarget | null, sidebar: HTMLElement): Tip | null {
  if (!(target instanceof Element)) return null
  if (sidebar.querySelector(KEY_WITH_OPEN_LIST)) return null
  const element = target.closest<HTMLElement>('[data-tip]')
  if (!element || !sidebar.contains(element) || !element.dataset.tip) return null
  const place: TipPlace = element.dataset.tipPlace === 'below' ? 'below' : 'beside'
  const sidebarRight = sidebar.getBoundingClientRect().right
  const position = sidebarTipPosition(element.getBoundingClientRect(), sidebarRight, place)
  return { label: element.dataset.tip, note: element.dataset.tipNote, keys: element.dataset.tipKeys, ...position, place }
}

/**
 * One tooltip for the whole sidebar (Bilanss SidebarTooltip), for whatever element with data-tip is under the
 * pointer or has keyboard focus. It is placed against the viewport because the sidebar's list scrolls and would clip
 * it. It appears and goes without moving, repeats a name the element already has (so it is hidden from assistive
 * technology), and Escape dismisses it.
 */
export function SidebarTooltip({ sidebar, resetKey }: { sidebar: RefObject<HTMLElement | null>; resetKey: string }) {
  const [tip, setTip] = useState<Tip | null>(null)

  // A change of layout moves everything the tooltip was pointing at.
  useEffect(() => setTip(null), [resetKey])

  useEffect(() => {
    const container = sidebar.current
    if (!container) return
    const panel = container
    const hide = () => setTip(null)
    function showForPointer(event: PointerEvent) {
      if (event.pointerType !== 'touch') setTip(tipFor(event.target, panel))
    }
    function showForKeyboard(event: FocusEvent) {
      const focused = event.target instanceof Element && event.target.matches(':focus-visible')
      setTip(focused ? tipFor(event.target, panel) : null)
    }
    function dismiss(event: KeyboardEvent) {
      if (event.key === 'Escape') hide()
    }
    panel.addEventListener('pointerover', showForPointer)
    panel.addEventListener('pointerleave', hide)
    panel.addEventListener('focusin', showForKeyboard)
    panel.addEventListener('focusout', hide)
    // Capture: the list inside the sidebar scrolls, and scroll events do not bubble.
    panel.addEventListener('scroll', hide, true)
    document.addEventListener('keydown', dismiss)
    return () => {
      panel.removeEventListener('pointerover', showForPointer)
      panel.removeEventListener('pointerleave', hide)
      panel.removeEventListener('focusin', showForKeyboard)
      panel.removeEventListener('focusout', hide)
      panel.removeEventListener('scroll', hide, true)
      document.removeEventListener('keydown', dismiss)
    }
  }, [sidebar])

  if (!tip) return null
  return (
    <div
      className={`sidebar-tooltip ${tip.place === 'below' ? 'is-below' : ''}`}
      aria-hidden="true"
      style={{ top: tip.top, left: tip.left }}
    >
      <span>{tip.label}</span>
      {tip.note && <span className="sidebar-tooltip-note">{tip.note}</span>}
      {tip.keys && <kbd>{tip.keys}</kbd>}
    </div>
  )
}
